// src/components/AddBusinessDetail.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AddBusinessDetail.css';
import API from '../api';

const timezones = ['EST', 'CST', 'MST', 'PST'];
const daysOfWeek = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const urlRegex = /^https?:\/\/.+$/i;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

const AddBusinessDetail = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');

  const businessId = localStorage.getItem('id');

  const [formData, setFormData] = useState({
    // Profile
    businessName: '',
    googleReviewUrl: '',
    coverImageUrl: '',
    addressMapUrl: '',
    businessEmail: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
    },

    // Owner
    firstName: '',
    lastName: '',
    phone: '',
    ownerAddress: '',
    ownerTimezone: '',

    // Schedule
    workingDays: [],
    hours: {},

    // Optional
    disabledDatesText: '',

    // Socials (optional)
    facebookUrl: '',
    instagramUrl: '',
    twitterUrl: '',
    xUrl: '',
    tiktokUrl: '',
    youtubeUrl: '',
  });

  // ---------- validation ----------
  const validate = (data, { skipPhoneVerified = true } = {}) => {
    const newErrors = {};

    // Business name
    if (!data.businessName.trim()) {
      newErrors.businessName = 'Business name is required.';
    }

    // URLs (with format hints)
    if (data.googleReviewUrl && !urlRegex.test(data.googleReviewUrl)) {
      newErrors.googleReviewUrl =
        'Enter a valid URL starting with http:// or https:// (e.g. https://search.google.com/...).';
    }

    if (data.addressMapUrl && !urlRegex.test(data.addressMapUrl)) {
      newErrors.addressMapUrl =
        'Enter a valid map URL starting with http:// or https:// (e.g. https://maps.google.com/...).';
    }

    if (data.facebookUrl && !urlRegex.test(data.facebookUrl)) {
      newErrors.facebookUrl =
        'Facebook URL must start with http:// or https:// (e.g. https://facebook.com/yourpage).';
    }

    if (data.instagramUrl && !urlRegex.test(data.instagramUrl)) {
      newErrors.instagramUrl =
        'Instagram URL must start with http:// or https:// (e.g. https://instagram.com/yourhandle).';
    }

    if (data.twitterUrl && !urlRegex.test(data.twitterUrl)) {
      newErrors.twitterUrl =
        'Twitter URL must start with http:// or https:// (e.g. https://twitter.com/yourhandle).';
    }

    if (data.xUrl && !urlRegex.test(data.xUrl)) {
      newErrors.xUrl =
        'X URL must start with http:// or https:// (e.g. https://x.com/yourhandle).';
    }

    if (data.tiktokUrl && !urlRegex.test(data.tiktokUrl)) {
      newErrors.tiktokUrl =
        'TikTok URL must start with http:// or https:// (e.g. https://tiktok.com/@yourname).';
    }

    if (data.youtubeUrl && !urlRegex.test(data.youtubeUrl)) {
      newErrors.youtubeUrl =
        'YouTube URL must start with http:// or https:// (e.g. https://youtube.com/@yourchannel).';
    }

    // Email
    if (data.businessEmail && !emailRegex.test(data.businessEmail)) {
      newErrors.businessEmail = 'Enter a valid email address (e.g. name@example.com).';
    }

    // Address (you can tighten these if you want them required)
    if (!data.address.line1.trim()) {
      newErrors['address.line1'] = 'Address Line 1 is required.';
    }
    if (!data.address.city.trim()) {
      newErrors['address.city'] = 'City is required.';
    }
    if (!data.address.state.trim()) {
      newErrors['address.state'] = 'State is required.';
    }
    if (!data.address.postalCode.trim()) {
      newErrors['address.postalCode'] = 'Postal code is required.';
    }
    if (!data.address.country.trim()) {
      newErrors['address.country'] = 'Country is required.';
    }

    // Owner
    if (!data.firstName.trim()) {
      newErrors.firstName = 'Owner first name is required.';
    }
    if (!data.lastName.trim()) {
      newErrors.lastName = 'Owner last name is required.';
    }

    // Phone
    if (!data.phone) {
      newErrors.phone = 'Owner phone is required.';
    } else if (!data.phone.startsWith('+1') || data.phone.length !== 12) {
      newErrors.phone =
        'Enter a valid US phone number in format +1XXXXXXXXXX (10 digits after +1).';
    }

    if (!data.ownerTimezone) {
      newErrors.ownerTimezone = 'Please select an owner timezone.';
    }

    // Working days
    if (!data.workingDays || data.workingDays.length === 0) {
      newErrors.workingDays = 'Select at least one working day.';
    }

    // Working hours (24h format enforced by <input type="time">, we just require values)
    if (data.workingDays && data.workingDays.length > 0) {
      data.workingDays.forEach((day) => {
        const dayHours = data.hours[day] || {};
        const opensAt = dayHours.opensAt || '';
        const closesAt = dayHours.closesAt || '';

        if (!opensAt) {
          newErrors[`hours.${day}.opensAt`] =
            'Opening time is required (24-hour, e.g. 09:00).';
        }
        if (!closesAt) {
          newErrors[`hours.${day}.closesAt`] =
            'Closing time is required (24-hour, e.g. 18:00).';
        }
        if (opensAt && closesAt && closesAt <= opensAt) {
          newErrors[`hours.${day}.closesAt`] =
            'Closing time must be later than opening time.';
        }
      });
    }

    // Disabled dates (YYYY-MM-DD, comma separated)
    if (data.disabledDatesText.trim()) {
      const parts = data.disabledDatesText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const bad = parts.find((p) => !dateRegex.test(p));
      if (bad) {
        newErrors.disabledDatesText =
          `Invalid date "${bad}". Use YYYY-MM-DD, separated by commas (e.g. 2025-05-15,2025-06-01).`;
      }
    }

    // OTP requirement (for save only)
    if (!skipPhoneVerified && !phoneVerified) {
      newErrors.phone =
        'Please verify this phone number via OTP before saving business details.';
    }

    return newErrors;
  };

  const revalidate = (updatedData, options) => {
    const v = validate(updatedData, options);
    setErrors(v);
    return v;
  };

  // ---------- cover image upload ----------
  const uploadCoverImage = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      setApiError('');
      const fd = new FormData();
      fd.append('coverImage', file);

      const resp = await API.post('/auth/upload/cover-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      let url = resp.data?.url;
      if (url) {
        url = url.replace(/^https?:\/\/[^/]+/, ''); // normalize to /uploads/...
        const updated = { ...formData, coverImageUrl: url };
        setFormData(updated);
        revalidate(updated);
      } else {
        setApiError('Upload failed: no URL returned from server.');
      }
    } catch (e) {
      console.error('Upload error', e);
      setApiError('Failed to upload cover image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const toggleDay = (day) => {
    const updatedDays = formData.workingDays.includes(day)
      ? formData.workingDays.filter((d) => d !== day)
      : [...formData.workingDays, day];

    const updated = { ...formData, workingDays: updatedDays };
    setFormData(updated);
    revalidate(updated);
  };

  const updateHours = (day, field, value) => {
    const updatedHours = {
      ...formData.hours,
      [day]: { ...(formData.hours[day] || {}), [field]: value },
    };
    const updated = { ...formData, hours: updatedHours };
    setFormData(updated);
    revalidate(updated);
  };

  const handleSubmit = async () => {
    setApiError('');

    if (!businessId) {
      setApiError('User not authenticated. Please log in again.');
      return;
    }

    const validationErrors = validate(formData, { skipPhoneVerified: false });
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setApiError('Please fix the errors highlighted below before saving.');
      return;
    }

    const disabledDates = (formData.disabledDatesText || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const payload = {
      businessId,

      // Profile
      businessName: formData.businessName,
      googleReviewUrl: formData.googleReviewUrl,
      coverImageUrl: formData.coverImageUrl,
      addressMapUrl: formData.addressMapUrl,
      businessEmail: formData.businessEmail,
      address: {
        line1: formData.address.line1,
        line2: formData.address.line2,
        city: formData.address.city,
        state: formData.address.state,
        postalCode: formData.address.postalCode,
        country: formData.address.country,
      },

      // Owner
      owner: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        address: formData.ownerAddress,
        timezone: formData.ownerTimezone,
      },

      // Schedule
      workingDays: formData.workingDays,
      workingHours: formData.hours,

      // Optional
      disabledDates,

      // Socials
      facebookUrl: formData.facebookUrl,
      instagramUrl: formData.instagramUrl,
      twitterUrl: formData.twitterUrl,
      xUrl: formData.xUrl,
      tiktokUrl: formData.tiktokUrl,
      youtubeUrl: formData.youtubeUrl,

      // Let backend know phone was verified
      phoneVerified: phoneVerified,
    };

    try {
      const token = localStorage.getItem('token');
      const res = await API.post('/auth/business-detail', payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      // If backend sends message/data, you could use it here
      alert('Business details saved!');
      navigate('/');
    } catch (err) {
      const data = err?.response?.data;
      console.error('❌ API error:', data || err);

      const newErrors = { ...errors };

      if (data?.fields && typeof data.fields === 'object') {
        Object.entries(data.fields).forEach(([key, msg]) => {
          newErrors[key] = msg;
        });
      }

      setErrors(newErrors);
      setApiError(data?.error || 'Failed to save business details. Please check the highlighted fields.');
    }
  };

  const stepTitles = {
    1: 'Business Profile',
    2: 'Business Address',
    3: 'Add Owner Details',
    4: 'Select Working Days',
    5: 'Apply Working Hours',
    6: 'Optional Settings',
  };

  return (
    <div className="add-form-page">
      <div className="add-form-card">
        <button className="back-link" onClick={() => navigate('/')}>
          ← Back to Home
        </button>

        <div className="stepper-wrapper">
          <div className="stepper-line" />
          <div className="stepper">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className="step-item" onClick={() => setStep(n)}>
                <div
                  className={`step-circle ${step === n ? 'active' : step > n ? 'completed' : ''}`}
                >
                  {step > n ? '✓' : n}
                </div>
                <span className="step-label">
                  {['Profile', 'Address', 'Owner', 'Days', 'Hours', 'Options'][n - 1]}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="step-header">
          <h2 className="step-title">{stepTitles[step]}</h2>
          <p className="step-subtitle">Step {step} of 6</p>
        </div>

        {apiError && <div className="api-error">{apiError}</div>}

        {/* ------------ STEP 1: BUSINESS PROFILE ------------ */}
        {step === 1 && (
          <div className="step-content">
            <div className="field">
              <label>Business Name *</label>
              <input
                value={formData.businessName}
                onChange={(e) => {
                  const updated = { ...formData, businessName: e.target.value };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
              {errors.businessName && (
                <p className="error-text">{errors.businessName}</p>
              )}
            </div>

            <div className="field">
              <label>Google Business Profile URL</label>
              <input
                value={formData.googleReviewUrl}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    googleReviewUrl: e.target.value,
                  };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
              <small className="helper-text">
                Search Your Business Name with city and state on Google &amp; click the share button  copy the URL from there.
              </small>
              {errors.googleReviewUrl && (
                <p className="error-text">{errors.googleReviewUrl}</p>
              )}
            </div>

            <div className="field">
              <label>Cover Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => uploadCoverImage(e.target.files?.[0])}
                disabled={uploading}
              />
               <small className="helper-text">
                Max Size :2 MB. Recommended dimensions: 1920x600 pixels.
               </small>
              {uploading && <p className="muted-text">Uploading...</p>}
              {formData.coverImageUrl && (
                <div className="cover-preview">
                  <img src={formData.coverImageUrl} alt="Cover preview" />
                </div>
              )}
            </div>

            {/* <div className="field">
              <label>Address Map URL</label>
              <input
                value={formData.addressMapUrl}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    addressMapUrl: e.target.value,
                  };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
              <small className="helper-text">
                Example: https://maps.google.com/...
              </small>
              {errors.addressMapUrl && (
                <p className="error-text">{errors.addressMapUrl}</p>
              )}
            </div> */}

            <div className="field">
              <label>Business Email</label>
              <input
                value={formData.businessEmail}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    businessEmail: e.target.value,
                  };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
              {errors.businessEmail && (
                <p className="error-text">{errors.businessEmail}</p>
              )}
            </div>
          </div>
        )}

        {/* ------------ STEP 2: ADDRESS ------------ */}
        {step === 2 && (
          <div className="step-content">
            <div className="field">
              <label>Address Line 1 *</label>
              <input
                value={formData.address.line1}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    address: { ...formData.address, line1: e.target.value },
                  };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
              {errors['address.line1'] && (
                <p className="error-text">{errors['address.line1']}</p>
              )}
            </div>

            <div className="field">
              <label>Address Line 2</label>
              <input
                value={formData.address.line2}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    address: { ...formData.address, line2: e.target.value },
                  };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
            </div>

            <div className="field-row">
              <div className="field">
                <label>City *</label>
                <input
                  value={formData.address.city}
                  onChange={(e) => {
                    const updated = {
                      ...formData,
                      address: { ...formData.address, city: e.target.value },
                    };
                    setFormData(updated);
                    revalidate(updated);
                  }}
                />
                {errors['address.city'] && (
                  <p className="error-text">{errors['address.city']}</p>
                )}
              </div>
              <div className="field">
                <label>State *</label>
                <input
                  value={formData.address.state}
                  onChange={(e) => {
                    const updated = {
                      ...formData,
                      address: { ...formData.address, state: e.target.value },
                    };
                    setFormData(updated);
                    revalidate(updated);
                  }}
                />
                {errors['address.state'] && (
                  <p className="error-text">{errors['address.state']}</p>
                )}
              </div>
            </div>

            <div className="field-row">
              <div className="field">
                <label>Postal Code *</label>
                <input
                  value={formData.address.postalCode}
                  onChange={(e) => {
                    const updated = {
                      ...formData,
                      address: {
                        ...formData.address,
                        postalCode: e.target.value,
                      },
                    };
                    setFormData(updated);
                    revalidate(updated);
                  }}
                />
                {errors['address.postalCode'] && (
                  <p className="error-text">
                    {errors['address.postalCode']}
                  </p>
                )}
              </div>
              <div className="field">
                <label>Country *</label>
                <input
                  value={formData.address.country}
                  onChange={(e) => {
                    const updated = {
                      ...formData,
                      address: {
                        ...formData.address,
                        country: e.target.value,
                      },
                    };
                    setFormData(updated);
                    revalidate(updated);
                  }}
                />
                {errors['address.country'] && (
                  <p className="error-text">{errors['address.country']}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ------------ STEP 3: OWNER ------------ */}
        {step === 3 && (
          <div className="step-content">
            <div className="field-row">
              <div className="field">
                <label>First Name *</label>
                <input
                  value={formData.firstName}
                  onChange={(e) => {
                    const updated = {
                      ...formData,
                      firstName: e.target.value,
                    };
                    setFormData(updated);
                    revalidate(updated);
                  }}
                />
                {errors.firstName && (
                  <p className="error-text">{errors.firstName}</p>
                )}
              </div>
              <div className="field">
                <label>Last Name *</label>
                <input
                  value={formData.lastName}
                  onChange={(e) => {
                    const updated = {
                      ...formData,
                      lastName: e.target.value,
                    };
                    setFormData(updated);
                    revalidate(updated);
                  }}
                />
                {errors.lastName && (
                  <p className="error-text">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Phone + OTP row */}
            <div className="field-row">
              <div className="field">
                <label>Phone (US, +1XXXXXXXXXX) *</label>
                <input
                  value={formData.phone}
                  onFocus={() => {
                    if (!formData.phone) {
                      const updated = { ...formData, phone: '+1' };
                      setFormData(updated);
                      revalidate(updated);
                    }
                  }}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (!value.startsWith('+1')) {
                      value = '+1' + value.replace(/\D/g, '');
                    }
                    const digitsOnly = value
                      .replace('+1', '')
                      .replace(/\D/g, '')
                      .slice(0, 10);
                    const updated = { ...formData, phone: '+1' + digitsOnly };
                    setFormData(updated);
                    setOtpSent(false);
                    setPhoneVerified(false);
                    revalidate(updated);
                  }}
                  onKeyDown={(e) => {
                    const cursorAtPrefix = e.target.selectionStart <= 2;
                    const isDeletingPrefix =
                      e.key === 'Backspace' || e.key === 'Delete';
                    if (cursorAtPrefix && isDeletingPrefix) e.preventDefault();
                  }}
                />
                {errors.phone && (
                  <p className="error-text">{errors.phone}</p>
                )}
              </div>

              <div className="field otp-btn-wrapper">
                <label>&nbsp;</label>
                {!otpSent && (
                  <button
                    className="btn-light btn-primary"
                    onClick={async () => {
                      setApiError('');
                      try {
                        await API.post('/auth/send-verification', {
                          phone: formData.phone,
                        });
                        setOtpSent(true);
                      } catch (err) {
                        console.error(err);
                        setApiError(
                          err?.response?.data?.error ||
                          'Failed to send OTP. Please check the phone number.'
                        );
                      }
                    }}
                    disabled={!formData.phone || !!errors.phone}
                  >
                    Send OTP
                  </button>
                )}
                {otpSent && !phoneVerified && (
                  <span className="muted-text">OTP sent to this phone</span>
                )}
              </div>
            </div>

            {otpSent && !phoneVerified && (
              <div className="field-row">
                <div className="field">
                  <label>Enter OTP</label>
                  <input
                    value={otpCode}
                    onChange={async (e) => {
                      const code = e.target.value;
                      setOtpCode(code);

                      if (code.length === 6) {
                        // Auto verify
                        setApiError('');
                        try {
                          setVerifying(true);
                          const res = await API.post('/auth/verify-code', {
                            phone: formData.phone,
                            code,
                          });

                          if (res.data.success) {
                            setPhoneVerified(true);
                            setErrors((prev) => {
                              const copy = { ...prev };
                              delete copy.otpCode;
                              delete copy.phone;
                              return copy;
                            });
                          } else {
                            setErrors((prev) => ({
                              ...prev,
                              otpCode: 'Invalid OTP. Please check and try again.',
                            }));
                          }
                        } catch (err) {
                          setApiError(
                            err?.response?.data?.error ||
                            'Verification failed. Please try again.'
                          );
                        } finally {
                          setVerifying(false);
                        }
                      }
                    }}
                    maxLength={6}
                  />

                  {errors.otpCode && (
                    <p className="error-text">{errors.otpCode}</p>
                  )}
                </div>
                <div className="field otp-btn-wrapper">
                  <label>&nbsp;</label>
                  <button
                    className="btn-outline"
                    onClick={async () => {
                      setApiError('');
                      if (!otpCode) {
                        setErrors((prev) => ({
                          ...prev,
                          otpCode: 'Please enter the OTP you received.',
                        }));
                        return;
                      }
                      try {
                        setVerifying(true);
                        const res = await API.post('/auth/verify-code', {
                          phone: formData.phone,
                          code: otpCode,
                        });
                        if (res.data.success) {
                          setPhoneVerified(true);
                          setErrors((prev) => {
                            const copy = { ...prev };
                            delete copy.otpCode;
                            delete copy.phone;
                            return copy;
                          });
                        } else {
                          setErrors((prev) => ({
                            ...prev,
                            otpCode: 'Invalid OTP. Please check and try again.',
                          }));
                        }
                      } catch (err) {
                        console.error(err);
                        setApiError(
                          err?.response?.data?.error ||
                          'Verification failed. Please try again.'
                        );
                      } finally {
                        setVerifying(false);
                      }
                    }}
                    disabled={verifying || !otpCode}
                  >
                    {verifying ? 'Verifying...' : 'Verify OTP'}
                  </button>
                </div>
              </div>
            )}

            {phoneVerified && (
              <p className="verified-text">✅ Phone number verified</p>
            )}

            {/* <div className="field">
              <label>Owner Address</label>
              <input
                value={formData.ownerAddress}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    ownerAddress: e.target.value,
                  };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
            </div> */}

            <div className="field">
              <label>Select Timezone *</label>
              <select
                value={formData.ownerTimezone}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    ownerTimezone: e.target.value,
                  };
                  setFormData(updated);
                  revalidate(updated);
                }}
              >
                <option value="">-- Select --</option>
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
              {errors.ownerTimezone && (
                <p className="error-text">{errors.ownerTimezone}</p>
              )}
            </div>
          </div>
        )}

        {/* ------------ STEP 4: WORKING DAYS ------------ */}
        {step === 4 && (
          <div className="step-content">
            <div className="field select-all">
              <label>
                <input
                  type="checkbox"
                  checked={formData.workingDays.length === daysOfWeek.length}
                  onChange={(e) => {
                    const updated = {
                      ...formData,
                      workingDays: e.target.checked ? [...daysOfWeek] : [],
                    };
                    setFormData(updated);
                    revalidate(updated);
                  }}
                />
                <span>Select All Days</span>
              </label>
            </div>

            <div className="days-grid">
              {daysOfWeek.map((day) => (
                <label key={day} className="day-pill">
                  <input
                    type="checkbox"
                    checked={formData.workingDays.includes(day)}
                    onChange={() => toggleDay(day)}
                  />
                  <span>{day}</span>
                </label>
              ))}
            </div>

            {errors.workingDays && (
              <p className="error-text">{errors.workingDays}</p>
            )}
          </div>
        )}

        {/* ------------ STEP 5: WORKING HOURS ------------ */}
        {step === 5 && (
          <div className="step-content">
            <p className="muted-text top-spacing">
              Times use 24-hour format (HH:MM). Example: 09:00, 14:30, 18:00.
            </p>

            <div className="field-row apply-all-row">
              <div className="field">
                <label>Apply Same Opening Time (24-hour)</label>
                <input
                  type="time"
                  step="60"
                  onChange={(e) => {
                    const opensAt = e.target.value;
                    const updated = { ...formData, hours: { ...formData.hours } };
                    formData.workingDays.forEach((day) => {
                      updated.hours[day] = {
                        ...(updated.hours[day] || {}),
                        opensAt,
                      };
                    });
                    setFormData(updated);
                    revalidate(updated);
                  }}
                />
              </div>
              <div className="field">
                <label>Apply Same Closing Time (24-hour)</label>
                <input
                  type="time"
                  step="60"
                  onChange={(e) => {
                    const closesAt = e.target.value;
                    const updated = { ...formData, hours: { ...formData.hours } };
                    formData.workingDays.forEach((day) => {
                      updated.hours[day] = {
                        ...(updated.hours[day] || {}),
                        closesAt,
                      };
                    });
                    setFormData(updated);
                    revalidate(updated);
                  }}
                />
              </div>
            </div>

            <div className="hours-list">
              {formData.workingDays.map((day) => (
                <div key={day} className="hours-row">
                  <div className="day-label">{day}</div>
                  <div className="field">
                    <input
                      type="time"
                      step="60"
                      value={formData.hours[day]?.opensAt || ''}
                      onChange={(e) =>
                        updateHours(day, 'opensAt', e.target.value)
                      }
                    />
                    {errors[`hours.${day}.opensAt`] && (
                      <p className="error-text">
                        {errors[`hours.${day}.opensAt`]}
                      </p>
                    )}
                  </div>
                  <span className="to-label">to</span>
                  <div className="field">
                    <input
                      type="time"
                      step="60"
                      value={formData.hours[day]?.closesAt || ''}
                      onChange={(e) =>
                        updateHours(day, 'closesAt', e.target.value)
                      }
                    />
                    {errors[`hours.${day}.closesAt`] && (
                      <p className="error-text">
                        {errors[`hours.${day}.closesAt`]}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ------------ STEP 6: OPTIONAL ------------ */}
        {step === 6 && (
          <div className="step-content">
            {/* <div className="field">
              <label>
                Disabled Dates (comma-separated, YYYY-MM-DD, e.g.
                2025-05-15,2025-07-12)
              </label>
              <input
                value={formData.disabledDatesText}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    disabledDatesText: e.target.value,
                  };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
              {errors.disabledDatesText && (
                <p className="error-text">{errors.disabledDatesText}</p>
              )}
            </div> */}

            <h4 className="section-subtitle">Social Links (optional)</h4>

            <div className="field">
              <label>Facebook URL</label>
              <input
                value={formData.facebookUrl}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    facebookUrl: e.target.value,
                  };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
              <small className="helper-text">
                Example: https://facebook.com/yourpage
              </small>
              {errors.facebookUrl && (
                <p className="error-text">{errors.facebookUrl}</p>
              )}
            </div>

            <div className="field">
              <label>Instagram URL</label>
              <input
                value={formData.instagramUrl}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    instagramUrl: e.target.value,
                  };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
              <small className="helper-text">
                Example: https://instagram.com/yourhandle
              </small>
              {errors.instagramUrl && (
                <p className="error-text">{errors.instagramUrl}</p>
              )}
            </div>

            <div className="field">
              <label>Twitter URL</label>
              <input
                value={formData.twitterUrl}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    twitterUrl: e.target.value,
                  };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
              <small className="helper-text">
                Example: https://twitter.com/yourhandle
              </small>
              {errors.twitterUrl && (
                <p className="error-text">{errors.twitterUrl}</p>
              )}
            </div>

            <div className="field">
              <label>X URL</label>
              <input
                value={formData.xUrl}
                onChange={(e) => {
                  const updated = { ...formData, xUrl: e.target.value };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
              <small className="helper-text">
                Example: https://x.com/yourhandle
              </small>
              {errors.xUrl && (
                <p className="error-text">{errors.xUrl}</p>
              )}
            </div>

            <div className="field">
              <label>TikTok URL</label>
              <input
                value={formData.tiktokUrl}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    tiktokUrl: e.target.value,
                  };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
              <small className="helper-text">
                Example: https://tiktok.com/@yourname
              </small>
              {errors.tiktokUrl && (
                <p className="error-text">{errors.tiktokUrl}</p>
              )}
            </div>

            <div className="field">
              <label>YouTube URL</label>
              <input
                value={formData.youtubeUrl}
                onChange={(e) => {
                  const updated = {
                    ...formData,
                    youtubeUrl: e.target.value,
                  };
                  setFormData(updated);
                  revalidate(updated);
                }}
              />
              <small className="helper-text">
                Example: https://youtube.com/@yourchannel
              </small>
              {errors.youtubeUrl && (
                <p className="error-text">{errors.youtubeUrl}</p>
              )}
            </div>


            {step === 6 && !phoneVerified && (
              <p className="error-text" style={{ marginTop: '8px' }}>
                You must verify the owner phone number with OTP (Step 3) before
                saving business details.
              </p>
            )}


          </div>
        )}

        {/* Navigation buttons */}
        <div className="navigation-buttons">
          {step > 1 ? (
            <button
              className="btn-secondary"
              onClick={() => setStep((s) => s - 1)}
            >
              Back
            </button>
          ) : (
            <span />
          )}



          {step < 6 && (
            <button
              className="btn-primary"
              onClick={() => setStep((s) => s + 1)}
            >
              Next
            </button>
          )}



          {step === 6 && (
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={!phoneVerified}
              title={
                phoneVerified
                  ? 'Save business details'
                  : 'Verify owner phone (step 3) before saving'
              }
            >
              Save
            </button>
          )}
        </div>


      </div>
    </div>
  );
};

export default AddBusinessDetail;
